// ---------------------------------------------------------------------------
// Reglage de l'animation du blason.
//
// La vague est decrite dans le SVG : un degrade dont la crete glisse le long de
// son axe (`animateTransform` sur `gradientTransform`). Trois choses se reglent
// donc a chaud, sans toucher au balisage :
//   - la GROSSEUR ecarte les paliers du degrade de part et d'autre de la crete ;
//   - la BRILLANCE agit sur l'opacite de ces memes paliers ;
//   - la VITESSE change la duree du cycle, ce qui impose de relancer
//     l'animation : SMIL ne reprend pas une duree modifiee en cours de route.
// ---------------------------------------------------------------------------

export const BLASON_DEFAUTS = { brillance: 100, vitesse: 100, grosseur: 100 };

// Profil de reference de la crete, a 100 % partout. `ecart` est la distance au
// centre en points de pourcentage, `opacite` la valeur du palier.
const PALIERS = [
  { ecart: -50, opacite: 0 },
  { ecart: -2.55, opacite: 0 },
  { ecart: -1.5, opacite: 0.14 },
  { ecart: -0.6, opacite: 0.8 },
  { ecart: 0, opacite: 1 },
  { ecart: 0.6, opacite: 0.8 },
  { ecart: 1.5, opacite: 0.14 },
  { ecart: 2.55, opacite: 0 },
  { ecart: 50, opacite: 0 },
];

const DUREE = 1.53; // secondes, a vitesse 100

const borne = (v, min, max) => Math.max(min, Math.min(max, Number(v) || 0));

/**
 * Applique les reglages a tous les blasons de la page. Sans argument, remet le
 * profil d'origine.
 */
export function appliquerBlason(r = BLASON_DEFAUTS) {
  const grosseur = borne(r.grosseur ?? 100, 20, 400) / 100;
  const brillance = borne(r.brillance ?? 100, 10, 200) / 100;
  const vitesse = borne(r.vitesse ?? 100, 20, 400) / 100;

  for (const grad of document.querySelectorAll('.embleme linearGradient')) {
    const stops = grad.querySelectorAll('stop');
    // Le degrade n'a de sens que si ses paliers sont au complet : on ne touche
    // pas a une forme qu'on ne reconnait pas.
    if (stops.length !== PALIERS.length) continue;
    stops.forEach((s, i) => {
      const p = PALIERS[i];
      const offset = Math.max(0, Math.min(100, 50 + p.ecart * (Math.abs(p.ecart) < 50 ? grosseur : 1)));
      s.setAttribute('offset', `${offset.toFixed(2)}%`);
      s.setAttribute('stop-opacity', String(Math.min(1, p.opacite * brillance).toFixed(3)));
    });
  }

  const duree = DUREE / vitesse;

  // La vague repart toujours du meme point : en bas a gauche du blason. Une
  // phase heritee de l'horloge la faisait apparaitre a mi-course selon le
  // moment du chargement, ce qui se voit immediatement en enchainant les
  // rechargements.
  const phase = 0;

  for (const svg of document.querySelectorAll('svg.embleme')) {
    try { svg.unpauseAnimations(); } catch { /* pas de SMIL */ }
  }

  for (const anim of document.querySelectorAll('.embleme animateTransform')) {
    // La seconde crete vit dans un degrade dont l'identifiant porte un suffixe :
    // c'est un repere stable, contrairement au `begin`, que l'on reecrit ici.
    anim.setAttribute('dur', `${duree.toFixed(3)}s`);
    anim.setAttribute('begin', `-${phase.toFixed(3)}s`);
    try {
      // Demarrer DANS LE PASSE place la vague a sa phase courante sans a-coup.
      anim.beginElementAt(-phase);
    } catch {
      try { anim.beginElement(); } catch { /* pas encore dans le document */ }
    }
  }
}

/** Lit les reglages depuis le stockage de preferences fourni. */
export function lireBlason(prefs) {
  return {
    brillance: Number(prefs.lire('blason-brillance', '100')) || 100,
    vitesse: Number(prefs.lire('blason-vitesse', '100')) || 100,
    grosseur: Number(prefs.lire('blason-grosseur', '100')) || 100,
  };
}
